import { useCallback, useState } from 'react';

import {
  getToolDefaultAssetType,
  getToolMixtureSubGenerationTools,
  isMixtureTool,
} from 'src/data/generationTool';
import { normalizeTraitButNot } from 'src/data/toneOfVoice';
import { testCustomToneOfVoice } from 'src/graphql/toneOfVoice';
import * as events from 'src/lib/events';
import { useErrorHandler } from 'src/services/useErrorHandler';
import { useTrackingEvent } from 'src/services/useTrackingEvent';
import { useCurrentCustomerId } from 'src/store/customer';

const getMixtureSubToolForAssetType = (mainGenerationTool, mixtureSubAssetType) => {
  if (!isMixtureTool(mainGenerationTool)) {
    return mainGenerationTool;
  }

  const mixtureTools = getToolMixtureSubGenerationTools(mainGenerationTool);
  return mixtureTools.find((tool) => getToolDefaultAssetType(tool) === mixtureSubAssetType);
};

const createTovInput = ({ toneOfVoice, trait, butNot }) => {
  const selectedTrait = toneOfVoice.traits.find((item) => item.trait === trait.trait);

  return {
    draftTov: {
      traits: [
        butNot
          ? {
              trait: '',
              butNotPriority: 1,
              butNot: normalizeTraitButNot(selectedTrait.butNot),
            }
          : { trait: selectedTrait.trait, traitPriority: 1 },
      ],
    },
  };
};

export const useApplyAnalyzedToneOfVoice = ({
  generationTool,
  localEditorRef,
  onApplyingAnalyzedToneOfVoice,
  mixtureSubAssetType,
  language,
  setSuccessMessage,
}) => {
  const handleError = useErrorHandler();
  const [applyingAnalyzedToneOfVoice, setApplyingAnalyzedToneOfVoice] = useState(null);

  const trackEvent = useTrackingEvent();

  const currentCustomerId = useCurrentCustomerId();

  const handleApplyAnalyzedToneOfVoice = useCallback(
    async ({ toneOfVoice, trait, butNot, structureInstructionsData }) => {
      if (onApplyingAnalyzedToneOfVoice) {
        onApplyingAnalyzedToneOfVoice();
      }
      setApplyingAnalyzedToneOfVoice(trait.trait);

      const html = localEditorRef.current?.getHtml();
      const tovInput = createTovInput({ toneOfVoice, trait, butNot });
      const alignedTool = getMixtureSubToolForAssetType(generationTool, mixtureSubAssetType);

      try {
        const result = await testCustomToneOfVoice({
          customerId: currentCustomerId,
          html,
          generationTool: alignedTool,
          tovInput,
          language,
          structureInstructionsData,
        });

        trackEvent(events.TONE_OF_VOICE.analyzedToneOfVoiceApplied, {
          page: events.EVENT_PAGE.editor,
          action: butNot ? 'less' : 'more',
          trait: butNot ? normalizeTraitButNot(trait.butNot) : trait.trait,
          html,
        });

        localEditorRef.current?.replaceHtml(result?.modifiedCopyHtml);

        const message = `Updated to be ${
          butNot ? `less ${normalizeTraitButNot(trait.butNot)}` : `more ${trait.trait}`
        }`;

        setSuccessMessage(message);
      } catch (error) {
        handleError(error);
      } finally {
        setApplyingAnalyzedToneOfVoice(null);
      }
    },
    [
      onApplyingAnalyzedToneOfVoice,
      localEditorRef,
      generationTool,
      mixtureSubAssetType,
      currentCustomerId,
      language,
      trackEvent,
      setSuccessMessage,
      handleError,
    ]
  );

  return {
    handleApplyAnalyzedToneOfVoice,
    applyingAnalyzedToneOfVoice,
  };
};
